/* eslint-disable react/jsx-no-useless-fragment */
import { useContext, useEffect, useState } from 'react'
import { MeContext } from 'contexts/me'
import { createSubscriber } from 'api/memberships'
import PrimaryButton from 'components/primaryButton'
import { MembershipSubscriptionConfirmModal } from 'components/MembershipSubscriptionModal'
import { useNavigate } from 'react-router-dom'
import { trackEvent } from 'analytics/trackFunctions'
import { toLocaleCurrency } from 'utils/dinero'
import Tag from 'components/Tag'
import {
  MEMBERSHIP_BENEFITS_LIST,
  SUBORIGIN_OPTIONS,
  SUBORIGIN_USER_PATHS
} from 'constants/membership'
// Icons
import { FireMembership } from 'assets/icons'
import SuspendedMembership from 'components/SuspendedMembership'
import WORDING from 'constants/wording'
import Skeleton from 'react-loading-skeleton'
import RoundedIcon from 'components/RoundedIcon'
import { MEMBERSHIP_PRICE } from 'config'

const { SUSPENDED_MEMBERSHIP_HUB } = WORDING

const InactiveMembership = ({
  suspendedMembership = false,
  origin = 'HUB'
}: {
  suspendedMembership?: boolean
  origin?: keyof typeof SUBORIGIN_USER_PATHS
}) => {
  const [openSubsConfirmModal, setOpenSubsConfirmModal] = useState(false)
  const [isSubscribingUser, setIsSubscribingUser] = useState(false)
  const navigate = useNavigate()
  const { me } = useContext(MeContext)

  useEffect(() => {
    if (suspendedMembership)
      trackEvent({
        eventName: 'bille_pantalla_beneficios_membresia_suspendida'
      })
  }, [])

  const handleSubscription = () => {
    setIsSubscribingUser(true)
    const subscriberData = {
      name: me.profile.name,
      surname: me.profile.surname,
      email: me.profile.email,
      identificationNumber: me.profile.identification_number,
      phone: me.profile.phone,
      userUuid: me.user.uuid,
      subOrigin: SUBORIGIN_OPTIONS[SUBORIGIN_USER_PATHS[origin]]
    }
    createSubscriber(subscriberData).then((res) => {
      if (!res.success || res.error) {
        navigate('/suscripcion/erronea')
      } else {
        navigate('/suscripcion/exitosa')
      }
      setIsSubscribingUser(false)
      setOpenSubsConfirmModal(false)
    })
  }

  return (
    <>
      <div className='flex flex-col items-center'>
        {!me ? (
          <Skeleton width={360} height={250} borderRadius={12} />
        ) : (
          <>
            {suspendedMembership ? (
              <SuspendedMembership
                bigIcon
                className='!bg-[#F8F8F6] h-[250px]'
                paragraphText={SUSPENDED_MEMBERSHIP_HUB}
                eventName='bille_boton_pagar_beneficios_membresia_suspendida'
              />
            ) : (
              <>
                <RoundedIcon
                  className='!h-12 !w-12'
                  icon={<FireMembership className='w-5' />}
                />
                <h2 className='text-xl text-center font-bold leading-[22px] mt-2'>
                  Suscribite a Wayni
                  <span className='text-green-800 font-bold'>+</span>
                  <br />
                  por $ {toLocaleCurrency(MEMBERSHIP_PRICE)}
                </h2>
                <Tag className='mx-auto my-4 font-bold'>
                  Comenzá a pagar a partir del 01 del próximo mes
                </Tag>
                <p className='text-2xs text-gray-900 font-medium text-center'>
                  Pago mensual por débito automático.
                </p>
              </>
            )}
          </>
        )}

        <div className='bg-white rounded-lg space-y-4 w-full border border-gray-500 p-3 my-6'>
          <h4 className='text-purple-900 text-xs font-bold mb-1'>Beneficios</h4>
          {MEMBERSHIP_BENEFITS_LIST.map((item) => (
            <li
              key={item.title}
              className='w-full flex items-start justify-start list-none'
            >
              <img className='shrink-0 w-4' src={item.img} alt={item.alt} />
              <div className='w-full ml-2 -mt-1'>
                <h6 className='text-black font-bold text-xs'>{item.title}</h6>
                <p className='text-black font-normal text-2xs'>
                  {item.description}
                </p>
                {item.clarification && (
                  <p className='text-[#7C7C7C] text-2xs'>
                    {item.clarification}
                  </p>
                )}
              </div>
            </li>
          ))}
        </div>

        {!suspendedMembership ? (
          <PrimaryButton
            className='my-4'
            type='button'
            buttonText='Suscribirme'
            primaryButtonFunction={() => {
              trackEvent({ eventName: 'bille_wayniplus_suscribirme' })
              setOpenSubsConfirmModal(true)
            }}
          />
        ) : null}
      </div>
      <MembershipSubscriptionConfirmModal
        isOpen={openSubsConfirmModal}
        setIsOpen={setOpenSubsConfirmModal}
        handleSubscription={handleSubscription}
        isLoading={isSubscribingUser}
      />
    </>
  )
}

export default InactiveMembership
